/**
 * <s-card-preview> — hover motion for a product card's 2nd media item.
 *
 *   data-kind="gif"   wraps an <img> showing the GIF's still frame; data-animated-src is swapped in on hover.
 *   data-kind="video" wraps a muted, looping <video preload="none"> with data-src; it loads and plays on
 *                     hover and pauses (rewinding to the start) when the pointer leaves.
 *
 * Only runs on devices with a real hover pointer (not phones) and never when the shopper prefers
 * reduced motion, so those visitors keep the still frame. Nothing heavy loads until the first hover.
 */
const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class SCardPreview extends HTMLElement {
  #ac;

  connectedCallback() {
    const card = this.closest('article') || this.parentElement;
    if (!card) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    card.addEventListener('pointerenter', () => this.#start(), opts);
    card.addEventListener('pointerleave', () => this.#stop(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #allowed() {
    return canHover.matches && !reducedMotion.matches;
  }

  #start() {
    if (!this.#allowed()) return;

    if (this.dataset.kind === 'gif') {
      const img = this.querySelector('img[data-animated-src]');
      if (img && img.src !== img.dataset.animatedSrc) img.src = img.dataset.animatedSrc;
      return;
    }

    const video = this.querySelector('video[data-src]');
    if (!video) return;
    if (!video.getAttribute('src')) video.src = video.dataset.src;
    video.play().catch(() => {});
  }

  #stop() {
    const video = this.querySelector('video');
    if (!video || video.paused) return;
    video.pause();
    video.currentTime = 0;
  }
}

if (!customElements.get('s-card-preview')) customElements.define('s-card-preview', SCardPreview);
